import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import CategoryTagsStyles from '@stories/Components/CategoryTags/CategoryTags.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.div `
  --contentColour: ${brand.primary.blue.dark};
  --bgColour: ${brand.global.white};
  --memberTextColour: ${brand.primary.purple.dark};
  --memberBorderColour: ${brand.primary.purple.dark};
  --memberBackground: transparent;

  ${transition('background-color, color', 0.25)};

  display: flex;
  flex-direction: column;
  border-radius: 20px;
  color: var(--contentColour);
  background-color: var(--bgColour);
  overflow: hidden;
  border: 1px solid ${brand.primary.blue.dark};

  &[data-is-featured='true'][data-has-image='false'] {
    --contentColour: ${brand.global.white};
    --bgColour: ${brand.primary.purple.light};
    --memberTextColour: ${brand.primary.purple.dark};
    --memberBorderColour: ${brand.primary.purple.dark};
    --memberBackground: ${brand.primary.grey};
    border: none;
  }

  a& {
    text-decoration: none;

    ${() => Image}, svg {
      ${transition('transform, background-color, color', 0.25)};
    }

    &:focus-visible,
    &:hover {
      --contentColour: ${brand.global.white};
      --bgColour: ${brand.primary.blue.dark};
      --memberTextColour: ${brand.global.white};
      --memberBorderColour: ${brand.global.white};

      &[data-is-featured='true'][data-has-image='false'] {
        --contentColour: ${brand.global.white};
        --bgColour: ${brand.primary.purple.dark};
        --memberTextColour: ${brand.primary.purple.dark};
        --memberBorderColour: ${brand.primary.purple.dark};
        --memberBackground: ${brand.primary.grey};
      }

      ${() => Image}, svg {
        transform: scale(1.05);
      }
    }
  }

  ${CategoryTagsStyles.Container} {
    margin-bottom: 12px;
  }
`;
const ImageContainer = styled.div `
  ${imageWrapperPreserveRatio(406, 230)};

  display: grid;
  place-content: center;
  position: relative;

  [data-is-featured='true'] & {
    @media ${from(Device.TabletLarge)} {
      aspect-ratio: 842 / 230;
    }
  }
`;
const Image = styled.img `
  ${imageCoverContainer()};

  /* Border radius duplicated for iOS */
  border-radius: 20px 20px 0 0;
`;
const MemberAccess = styled.div `
  ${transition('border-color, color', 0.25)};
  ${fonts.lato.bold};
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: ${rgba(brand.primary.purple.light, 0.5)};
  color: ${brand.global.white};
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(14, 20)};
  text-transform: uppercase;
  padding: ${fluid(6, 6)} ${fluid(10, 14)};
  border-radius: 300000000px;
  backdrop-filter: blur(2px);
`;
const MemberIcon = styled.div `
  flex: 0 0 20px;
  height: 20px;
  width: 20px;
`;
const ContentWrapper = styled.div `
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${fluid(16, 20)} ${fluid(16, 16)} 16px;

  @media ${from(Device.Tablet)} {
    padding-bottom: 10px;
  }

  ${MemberAccess} {
    position: relative;
    top: auto;
    left: auto;
    width: fit-content;
    background-color: var(--memberBackground);
    border: 1px solid var(--memberBorderColour);
    color: var(--memberTextColour);
    margin-bottom: 11px;
  }

  ${ButtonStyles.Container} {
    --buttonHeight: ${fluid(42, 56)};
    position: relative;
    align-self: flex-end;

    [data-is-featured='true'][data-has-image='false'] & {
      --bgColour: transparent;
      --bgColourActive: ${brand.global.white};
      --bgColourHover: var(--colourText);
      --borderColorActive: var(--bgColourActive);
      --borderColorHover: var(--bgColourHover);
      --borderColour: var(--colourText);
      --colourText: ${brand.global.white};
      --colourTextHover: ${brand.primary.purple.dark};
    }
  }
`;
const ContentArea = styled.div `
  flex: 1 0 auto;
  padding-bottom: ${fluid(16, 24)};
`;
const Heading = styled.h3 `
  ${fonts.lato.semiBold};

  font-size: ${fluid(16, 22)};
  line-height: ${fluid(24, 30)};
  margin: 0;
`;
const DateTime = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: ${fluid(12, 20)} 0 0;
`;
const Date = styled.div `
  ${fonts.lato.semiBold};
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(22, 26)};
  color: var(--contentColour);

  svg {
    width: 24px;
    height: 24px;
  }
`;
const Time = styled.div `
  ${fonts.lato.semiBold};
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(22, 26)};
  color: var(--contentColour);

  svg {
    width: 24px;
    height: 24px;
  }
`;
const Location = styled.div `
  ${fonts.lato.semiBold};
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(22, 26)};
  color: var(--contentColour);

  svg {
    width: 24px;
    height: 24px;
  }
`;
const BaseContentArea = styled.div `
  display: flex;
  justify-content: space-between;
  gap: 16px;

  &[data-just-button='false'] {
    justify-content: flex-end;
  }

  ${Date} {
    ${fonts.lato.bold};
  }
`;
const ContentCardStyles = {
    BaseContentArea,
    Container,
    ContentArea,
    ContentWrapper,
    Date,
    DateTime,
    ImageContainer,
    Image,
    Heading,
    Location,
    MemberAccess,
    MemberIcon,
    Time,
};
export default ContentCardStyles;
